import styled, { createGlobalStyle, ThemeProps } from 'styled-components';
import { ThemeType } from '../../../theme/themeType';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  padding: 0;
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding: 0 4px;
  }
`;

const HeaderRow = styled.header`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  @media (min-width: 600px) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
  }
`;

const PreviewContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Title = styled.h2`
  font-size: 20px;
  margin: 0;
  cursor: default;

  @media (min-width: 600px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  font-size: 14px;
  margin: 0;
  color: ${(props) => props.theme.colors.inactive};
`;

const Content = styled.main`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: ${(props) => props.theme.colors.text};
`;

const AudioFilesSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 0;
`;

const AudioCardList = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 4px;
  min-height: 0;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.colors.outline};
    border-radius: 3px;
  }
`;

const DevicesSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 0;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
`;

const SectionTitleWithCount = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: ${(props) => props.theme.colors.text};
`;

const LinkButton = styled.button<{ $variant: 'selected' | 'queued' }>`
  font-size: 14px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${(props) => (props.$variant === 'selected' ? props.theme.colors.main_cta : '#FF0505')};
  text-decoration: none;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }
  &:focus {
    outline: none;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DeviceTagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  padding: 12px 0px;
  min-height: 80px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.colors.outline};
    border-radius: 3px;
  }
`;

const DeviceTag = styled.span<{ $variant: 'selected' | 'queued'; $isSelectedForSend?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 6px;
  height: 36px;
  font-size: 13px;
  font-weight: 500;
  max-width: 100%;
  border: ${(props) => (props.$variant === 'selected' ? 'none' : '1px solid #FF0505')};
  background: ${(props) => (props.$variant === 'selected' ? '#FEFFC1' : '#FFF1F1')};
  color: ${(props) => props.theme.colors.black};
  cursor: ${(props) => {
    if (props.$variant === 'selected' && props.$isSelectedForSend === false) return 'pointer';
    return 'default';
  }};
  opacity: ${(props) => (props.$variant === 'selected' && props.$isSelectedForSend === false ? 0.5 : 1)};
`;

const DeviceTagLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 80px;
  line-height: 20px;
`;

const DeviceTagRemove = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  border-radius: 2px;
  font-size: 14px;
  line-height: 1;

  &:hover:not(:disabled) {
    opacity: 0.8;
  }
  &:focus {
    outline: none;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SearchWrap = styled.div`
  width: 100%;
  max-width: 190px;
`;

const SearchInputWrap = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInputField = styled.input`
  width: 100%;
  height: 44px;
  padding: 12px 40px 12px 16px;
  box-sizing: border-box;
  border-radius: 12px;
  border: 1px solid ${(props) => props.theme.colors.outline};
  background-color: ${(props) => props.theme.colors.widget_background};
  color: ${(props) => props.theme.colors.text};
  font-size: 13px;
  caret-color: ${(props) => props.theme.colors.main_cta};

  ::placeholder {
    color: ${(props) => props.theme.colors.inactive};
  }

  :focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.main_cta};
  }

  :disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const SearchIconWrap = styled.span`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: ${(props) => props.theme.colors.inactive};
`;

const EmptyMessage = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${(props) => props.theme.colors.inactive};
`;

const SendButtonWrap = styled.div`
  margin-top: 8px;
`;

const TimeoutSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 0;
`;

const TimeoutLabel = styled.label`
  font-size: 16px;
  font-weight: 600;
  padding-bottom: 8px;
  color: ${(props) => props.theme.colors.text};
  margin: 0;
`;

const DatePickerInputWrap = styled.div`
  position: relative;
  width: 100%;
`;

const DatePickerIconWrap = styled.button`
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  cursor: pointer;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  color: ${(props) => props.theme.colors.inactive};
`;

const DatePickerWrap = styled.div`
  width: 100%;
  max-width: 190px;

  &.assign-announcement-timeout .react-datepicker-wrapper {
    display: block;
    width: 100%;
  }
  &.assign-announcement-timeout .react-datepicker__input-container {
    display: block;
    width: 100%;
  }
  &.assign-announcement-timeout .react-datepicker-time__header {
    color: ${(props) => props.theme.colors.text};
  }
  &.assign-announcement-timeout .react-datepicker__input-container input {
    width: 100%;
    height: 44px;
    padding: 12px 40px 12px 16px;
    box-sizing: border-box;
    border-radius: 12px;
    border: 1px solid ${(props) => props.theme.colors.outline};
    background-color: ${(props) => props.theme.colors.widget_background};
    color: ${(props) => props.theme.colors.text};
    font-size: 13px;
    caret-color: ${(props) => props.theme.colors.main_cta};
  }

  &.assign-announcement-timeout .react-datepicker__input-container input::placeholder {
    color: ${(props) => props.theme.colors.inactive};
  }

  &.assign-announcement-timeout .react-datepicker__input-container input:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.main_cta};
  }
`;

export const AssignAnnouncementDatePickerGlobal = createGlobalStyle`
  .assign-announcement-timeout-popper.react-datepicker-popper .react-datepicker {
    font-family: inherit;
    padding: 16px;
    display: flex;
    border: 1px solid ${(props: ThemeProps<ThemeType>) => props.theme.colors.outline};
    border-radius: 12px;
    background-color: ${(props: ThemeProps<ThemeType>) => props.theme.colors.widget_background};
    box-shadow: ${(props: ThemeProps<ThemeType>) => props.theme.shadows.dropdown};
  }

  .assign-announcement-timeout-popper.react-datepicker-popper .react-datepicker__header {
    background-color: transparent;
    border-bottom: 1px solid ${(props: ThemeProps<ThemeType>) => props.theme.colors.outline};
    padding-bottom: 8px;
  }

  .assign-announcement-timeout-popper.react-datepicker-popper .react-datepicker__current-month,
  .assign-announcement-timeout-popper.react-datepicker-popper .react-datepicker__day-name {
    color: ${(props: ThemeProps<ThemeType>) => props.theme.colors.text};
  }

  .assign-announcement-timeout-popper.react-datepicker-popper .react-datepicker__navigation-icon::before {
    border-color: ${(props: ThemeProps<ThemeType>) => props.theme.colors.inactive};
  }

  .assign-announcement-timeout-popper.react-datepicker-popper .react-datepicker__day {
    color: ${(props: ThemeProps<ThemeType>) => props.theme.colors.inactive};
  }

  .assign-announcement-timeout-popper.react-datepicker-popper .react-datepicker__day:hover {
    background-color: ${(props: ThemeProps<ThemeType>) => props.theme.colors.widget_hover_background};
  }

  .assign-announcement-timeout-popper.react-datepicker-popper .react-datepicker__day--disabled {
    color: ${(props: ThemeProps<ThemeType>) => props.theme.colors.inactive};
    opacity: 0.5;
  }

  .assign-announcement-timeout-popper.react-datepicker-popper .react-datepicker__day--keyboard-selected,
  .assign-announcement-timeout-popper.react-datepicker-popper .react-datepicker__day--selected {
    background-color: ${(props: ThemeProps<ThemeType>) => props.theme.colors.main_cta};
    color: ${(props: ThemeProps<ThemeType>) => props.theme.colors.black};
  }

  .assign-announcement-timeout-popper .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--selected {
    background-color: ${(props: ThemeProps<ThemeType>) => props.theme.colors.main_cta};
    color: ${(props: ThemeProps<ThemeType>) => props.theme.colors.black};
  }

  .assign-announcement-timeout-popper .react-datepicker__navigation--next--with-time:not(.react-datepicker__navigation--next--with-today-button) {
    right: 96px;
  }

  .assign-announcement-timeout-popper.react-datepicker-popper .react-datepicker__time-container .react-datepicker__time-box {
    width: 85px;
    background-color: ${(props: ThemeProps<ThemeType>) => props.theme.colors.widget_background};
  }

  .assign-announcement-timeout-popper.react-datepicker-popper .react-datepicker__time-list-item {
    color: ${(props: ThemeProps<ThemeType>) => props.theme.colors.inactive};
  }

  .assign-announcement-timeout-popper.react-datepicker-popper .react-datepicker__time-list-item:hover:not(.react-datepicker__time-list-item--disabled) {
    background-color: ${(props: ThemeProps<ThemeType>) => props.theme.colors.widget_hover_background};
    color: ${(props: ThemeProps<ThemeType>) => props.theme.colors.text};
  }

  .assign-announcement-timeout-popper.react-datepicker-popper .react-datepicker__time-list-item--disabled {
    color: ${(props: ThemeProps<ThemeType>) => props.theme.colors.inactive};
    opacity: 0.2;
  }

  .assign-announcement-timeout-popper.react-datepicker-popper .react-datepicker__time-container {
    border-left: 1px solid ${(props: ThemeProps<ThemeType>) => props.theme.colors.outline};
  }

  .assign-announcement-timeout-popper.react-datepicker-popper .react-datepicker__header--time {
    color: ${(props: ThemeProps<ThemeType>) => props.theme.colors.text};
  }

  .assign-announcement-timeout-popper.react-datepicker-popper .react-datepicker__time-box,
  .assign-announcement-timeout-popper.react-datepicker-popper .react-datepicker__time-list {
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background: ${(props: ThemeProps<ThemeType>) => props.theme.colors.widget_background};
    }
    &::-webkit-scrollbar-thumb {
      background-color: ${(props: ThemeProps<ThemeType>) => props.theme.colors.inactive};
      border-radius: 20px;
    }
  }
`;

export default {
  Wrapper,
  HeaderRow,
  PreviewContainer,
  TitleBlock,
  Title,
  Subtitle,
  Content,
  SectionTitle,
  AudioFilesSection,
  AudioCardList,
  DevicesSection,
  SectionHeader,
  SectionTitleWithCount,
  LinkButton,
  DeviceTagList,
  DeviceTag,
  DeviceTagLabel,
  DeviceTagRemove,
  SearchWrap,
  SearchInputWrap,
  SearchInputField,
  SearchIconWrap,
  EmptyMessage,
  SendButtonWrap,
  TimeoutSection,
  TimeoutLabel,
  DatePickerWrap,
  DatePickerInputWrap,
  DatePickerIconWrap,
};
